import React, { useState } from 'react';

import { FaQuestionCircle } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import { Button } from '@techstyle/react-components';
import {
  defineMessages,
  FormattedMessage,
  useIntl,
} from '@techstyle/react-intl';

import { BasicModal } from '../../styles/modal';
import { useMembership } from '../../utils/useMembership';
import { Component as CloseButton } from '../CloseButton';
import { Component as Price } from '../ExtendedPrice';
import { withShoppingBagRevampUX } from '../MemberCreditsCartToggle/WithShoppingBagRevampUX';
import { Component as XtraVipIcon } from '../XtraVipIcon';

const { gotItLabel } = defineMessages({
  gotItLabel: {
    id: 'site_account_rewards.got_it_button',
    defaultMessage: 'got it!',
  },
});

const duration = 100;

const FaQuestionCircleStyled = styled(FaQuestionCircle)`
  cursor: pointer;
  margin-left: 3px;
  color: ${({ theme }) => theme.colors.lavender500};
  ${({ theme, isAfter }) => isAfter && `margin-left: ${theme.spacing.small}px;`}
  ${({ theme, isShoppingBagRevampUX }) =>
    isShoppingBagRevampUX &&
    `
    margin-left: 6px;
    margin-right: 12px;
    height: 16px;
    width: 16px;
    color: ${theme.colors.memberOffers};
  `}
`;

const StyledFaQuestionCircle = withShoppingBagRevampUX(FaQuestionCircleStyled);

const dialogStyle = css`
  width: 100%;
  box-shadow: 0px -2px 8px 0px ${({ theme }) => theme.colors.lightShadow3};
  border-radius: 16px;
  margin: ${props => props.theme.spacing.small}px;
  max-width: 414px;
  box-shadow: 0px 4px 8px 0px ${({ theme }) => theme.colors.lightShadow3};
`;
const overlayStyle = css`
  transition: opacity ${duration}ms ease-in-out;
`;

const StyledModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, 160px, 0)',
  },
})`
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

const ModalCloseButton = styled(CloseButton)`
  line-height: 50px;
  width: 50px;
  height: 50px;
`;

const StyledXtraVipIcon = styled(XtraVipIcon)`
  display: block;
  width: 48px;
  height: unset;
`;

const Content = styled.div`
  padding: ${props => props.theme.spacing.moderate}px;
`;

const Title = styled.div`
  padding: ${props => props.theme.spacing.moderate}px 0
    ${props => props.theme.spacing.small}px;
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.theme.colors.black};
  ${({ theme }) => theme.subHeader.variants.subHeading2Uppercase.textStyles}
`;

const ItemWrapper = styled.ul`
  list-style: disc;
  padding-left: 20px;
`;

const Item = styled.li`
  color: ${props => props.theme.colors.default};
  margin-bottom: ${props => props.theme.spacing.small}px;
  font-size: 14px;
  line-height: 22px;
  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles}
`;

const ButtonWrapper = styled.div`
  text-align: center;
`;

const ButtonLink = styled(Button)`
  ${({ theme }) => theme.button.defaultStyle};
  border: none;
  background-color: unset;
  color: ${props => props.theme.colors.active};
  font-size: 14px;
  font-weight: 600px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  ${({ theme }) => theme.button.variants.primaryLight.buttonStyle}
`;

function MemberCreditBenefitsModal({ isAfter }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { price: membershipPrice } = useMembership();

  const handleExit = () => {
    setIsModalOpen(false);
  };

  const handleShow = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <StyledFaQuestionCircle
        isAfter
        data-testid="member-credits-modal-button"
        onClick={handleShow}
      />

      <StyledModal
        isOpen={isModalOpen}
        closeButton={<ModalCloseButton />}
        closeTimeoutMS={duration}
        onExit={handleExit}
        overlayStyle={overlayStyle}
        dialogStyle={dialogStyle}
        titleId="member-credits-modal"
      >
        <Content>
          <StyledXtraVipIcon v2 />
          <Title>
            <FormattedMessage
              id="site_account_rewards.membership_and_member_credits_title"
              defaultMessage="Membership & Member Credits"
            />
          </Title>
          <ItemWrapper>
            <Item>
              <FormattedMessage
                id="site_account_rewards.dont_skip_the_month_payment"
                defaultMessage="Each month, you have the freedom to unlock more rewards for {formattedMembershipPrice}, including a Member Credit that never expires. Member Credits apply to purchases of {formattedMembershipPrice}+ (including tax and shipping)!"
                values={{
                  formattedMembershipPrice: <Price amount={membershipPrice} />,
                }}
              />
            </Item>
            <Item>
              <FormattedMessage
                id="site_account_rewards.each_member_credit"
                defaultMessage="After every 5th billing, you’ll receive a complimentary underwear pack or bra!"
              />
            </Item>
            <Item>
              <FormattedMessage
                id="site_account_rewards.save_up_to_fifty"
                defaultMessage="Don't want to be billed? 'Skip The Month' by the 5th of each month to avoid the charge on the 6th."
              />
            </Item>
          </ItemWrapper>
          <ButtonWrapper>
            <ButtonLink
              aria-label={formatMessage(gotItLabel)}
              onClick={handleExit}
            >
              <FormattedMessage {...gotItLabel} />
            </ButtonLink>
          </ButtonWrapper>
        </Content>
      </StyledModal>
    </>
  );
}

export default MemberCreditBenefitsModal;
